import React, { useState, useEffect } from "react";
import axios from "axios";
import "./Login.css";
import Footer from "../components/Footer";
import UserAgreementModal from "../components/UserAgreementModal";
import "../style.css";
import { getPageTitle } from '../constants'
import { setAuth } from '../Utils'

interface LoginProps {
  domain: string;
  login: boolean;
}

const LoginPage: React.FC<LoginProps> = ({ domain, login = true}) => {
  const [alias, setAlias] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [message, setMessage] = useState("");
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem('rememberMe') === 'true');
  const [showAgreement, setShowAgreement] = useState(false);

  useEffect(() => {
    document.title = login ? getPageTitle('Login') : getPageTitle('Registration');

    const backgroundImage = login ? `url("/images/Team_SanDiego_WNIT-1-620x372.jpg")` : `url("/images/football2.jpg")`;
    document.body.style.backgroundImage = backgroundImage;

    return () => {
      document.body.style.backgroundImage = "";
    };
  }, [login]);


  const handleError = (error: any) => {
    const data = error.response?.data;
    const errorMsg = typeof data === 'object' ? (data?.message || data?.error || JSON.stringify(data)) : data;
    setMessage(`${error.response?.status || "Error"}: ${errorMsg || error.message}`);
  };

  const handleLogin = async () => {
    try {
      const response = await axios.post(`/api/auth/login`, {
        username: email,
        password,
      }, { withCredentials: true });
      if (response.status === 200) {
        const accessToken = response.headers['accesstoken'] || response.data.token;
        const refreshToken = response.headers['refreshtoken'] || null;

        // Extract role from JWT payload
        let role = 'user';
        try {
          const payload = JSON.parse(atob(accessToken.split('.')[1]));
          const authorities: string[] = payload.authorities || [];
          role = authorities.includes('ROLE_ADMIN') ? 'admin' : 'user';
        } catch {
          role = response.data.role || 'user';
        }

        setAuth(accessToken, refreshToken, role, rememberMe);
        window.location.href = "/diary";
      }
    } catch (error: any) {
      handleError(error);
    }
  };

  const handleRegister = async () => {
    if (!agreementAccepted) {
      setMessage(translations["userAgreementNotAccepted"]);
      return;
    }
    try {
      const response = await axios.post(`${domain}/enroll`, {
        alias: alias || "defaultAlias",
        email,
        password,
      });
      setMessage(response.data.message);
      setAlias("");
      setEmail("");
      setPassword("");
    } catch (error: any) {
      handleError(error);
    }
  };

  const handleSubmit = () => {
    if (login) {
      handleLogin();
    } else {
      handleRegister();
    }
  };

  return (
    <div id="wrap">
      <a href="/" id="back-button" title="Back to home">
        <span>←</span> Home
      </a>
      <div id="form-div">
        <form id="form-signin" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}>
          <div className="auth-tabs">
            <a href="/login" className={`auth-tab ${login ? 'active' : ''}`}>
              {translations["message.nav.login"]}
            </a>
            <a href="/registration" className={`auth-tab ${!login ? 'active' : ''}`}>
              {translations["message.nav.registration"]}
            </a>
          </div>

          {message && <div id="message" style={{ fontWeight: "bold", backgroundColor: "#FF1945" }}>{message}</div>}

          {!login && (
            <>
              <label htmlFor="alias">{translations["alias.set.info"]}</label>
              <input
                className="login-input"
                id="alias"
                name="alias"
                placeholder={translations["user.info.alias"]}
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                required
              />
            </>
          )}

          <label htmlFor="email">{translations["email.set.info"]}</label>
          <input
            className="login-input"
            id="email"
            name={login ? "j_username" : "email"}
            placeholder={translations["user.info.email"]}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <label htmlFor="password">{translations["password.set.info"]}</label>
          <input
            className="login-input"
            id="password"
            name={login ? "j_password" : "password"}
            placeholder={translations["user.info.password"]}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            required
          />

          {login ? (
            <div className="checkbox">
              <label>
                <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}/>
                <span>{translations["message.nav.remember"]}</span>
              </label>
            </div>
          ) : (
            <div className="checkbox">
              <label>
                <input
                  type="checkbox"
                  id="agreement_accepted"
                  checked={agreementAccepted}
                  onChange={(e) => setAgreementAccepted(e.target.checked)}
                />
                <span>
                  {(translations["form.user_agreement"] || "").split("{0}")[0] /* fallback needed for split */}
                  <a href="#agreement" onClick={(e) => { e.preventDefault(); setShowAgreement(true); }}>
                    {translations["form.user_agreement_name"]}
                  </a>
                  {(translations["form.user_agreement"] || "").split("{0}")[1] /* fallback needed for split */}
                </span>
              </label>
            </div>
          )}

          <a
            id="submit-login-button"
            className="login-submit"
            type="submit"
            onClick={handleSubmit}
            style={{ marginTop: "5px" }}
          >
            {login ? translations["message.nav.login"] : translations["message.nav.registration"]}
          </a>

          {login && (
            <div style={{ fontWeight: "bold" }}>
              <a id="reset-button" href="/reset">{translations["password.forget.question"]}</a>
            </div>
          )}
        </form>
      </div>
      <Footer onTranslationsLoaded={setTranslations} onAgreementClick={() => setShowAgreement(true)}/>
      <UserAgreementModal
        open={showAgreement}
        onClose={() => setShowAgreement(false)}
        title={translations["footer.userAgreement"]}
      />
    </div>
  );
};

export default LoginPage;